import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvents } from "@/lib/hooks/use-events";
import {
  trainingAttemptKey,
  trainingHomeKey,
} from "@/lib/api/training-simulator/training-simulator";
import type {
  TrainingSimulatorAttemptCompletedPayload,
  TrainingSimulatorMessageCreatedPayload,
  TrainingSimulatorReplyFailedPayload,
} from "@/lib/types/events";

interface UseTrainingSessionEventsResult {
  /** Thread IDs whose latest AI reply job exhausted retries. */
  replyFailedThreadIds: ReadonlySet<string>;
  resetReplyFailed: (threadId: string | undefined) => void;
}

/**
 * Subscribes to simulator SSE events for a single attempt and reconciles the
 * React Query cache. A successful teen reply clears any prior failure state.
 */
export function useTrainingSessionEvents(
  attemptId: string,
): UseTrainingSessionEventsResult {
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();
  const [replyFailedThreadIds, setReplyFailedThreadIds] = useState<
    ReadonlySet<string>
  >(new Set());

  useEffect(() => {
    // Invalidating the attempt key prefix-matches its `messages` child, so a
    // single call refreshes both the attempt and its transcript.
    const invalidateAttempt = () =>
      queryClient.invalidateQueries({
        queryKey: trainingAttemptKey(attemptId),
      });

    const unsubscribeMessage =
      addEventListener<TrainingSimulatorMessageCreatedPayload>(
        "TRAINING_SIMULATOR_MESSAGE_CREATED",
        (payload) => {
          if (payload.attemptId !== attemptId) return;
          setReplyFailedThreadIds((threadIds) => {
            if (!threadIds.has(payload.threadId)) return threadIds;

            const nextThreadIds = new Set(threadIds);
            nextThreadIds.delete(payload.threadId);
            return nextThreadIds;
          });
          invalidateAttempt();
        },
      );

    const unsubscribeCompleted =
      addEventListener<TrainingSimulatorAttemptCompletedPayload>(
        "TRAINING_SIMULATOR_ATTEMPT_COMPLETED",
        (payload) => {
          if (payload.attemptId !== attemptId) return;
          invalidateAttempt();
          queryClient.invalidateQueries({ queryKey: trainingHomeKey });
        },
      );

    const unsubscribeFailed =
      addEventListener<TrainingSimulatorReplyFailedPayload>(
        "TRAINING_SIMULATOR_REPLY_FAILED",
        (payload) => {
          if (payload.attemptId !== attemptId) return;
          setReplyFailedThreadIds((threadIds) => {
            if (threadIds.has(payload.threadId)) return threadIds;
            return new Set(threadIds).add(payload.threadId);
          });
        },
      );

    return () => {
      unsubscribeMessage();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [addEventListener, attemptId, queryClient]);

  const resetReplyFailed = useCallback((threadId: string | undefined) => {
    if (!threadId) return;

    setReplyFailedThreadIds((threadIds) => {
      if (!threadIds.has(threadId)) return threadIds;

      const nextThreadIds = new Set(threadIds);
      nextThreadIds.delete(threadId);
      return nextThreadIds;
    });
  }, []);

  return { replyFailedThreadIds, resetReplyFailed };
}
